import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Divider,
  IconButton,
  Button,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import ImageIcon from "@mui/icons-material/Image";
import moment from "moment";

export default function SessionEnhancementDialog({
  open,
  onClose,
  sessions = [],
  patientName = "",
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: 2 },
          border: "1px solid var(--color-border-soft)",
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          py: 1.1,
          px: 1.5,
          borderBottom: "1px solid var(--color-border-soft)",
          background: "#f8fbff",
        }}
      >
        <Box>
          <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: "var(--color-heading)" }}>
            Treatment Enhancements
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.76rem" }}>
            {patientName}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          p: { xs: 1.1, sm: 1.4 },
          backgroundColor: "#fcfdff",
          maxHeight: { xs: "calc(100vh - 118px)", sm: "70vh" },
        }}
      >
        {sessions.length === 0 ? (
          <Typography color="text.secondary" sx={{ fontSize: "0.82rem" }}>
            No enhancement images available
          </Typography>
        ) : (
          sessions.map((session, idx) => (
            <Box
              key={idx}
              sx={{
                mb: 1.2,
                p: 1.1,
                borderRadius: 1.5,
                border: "1px solid var(--color-border-soft)",
                backgroundColor: "#fff",
              }}
            >
              {/* Session Header */}
              <Box sx={{ display: "flex", alignItems: "center", mb: 0.8 }}>
                <ImageIcon color="primary" sx={{ mr: 0.7, fontSize: 18 }} />
                <Typography sx={{ fontSize: "0.86rem", fontWeight: 700, color: "var(--color-heading)" }}>
                  Session {session.sessionNo}
                </Typography>
                <Typography sx={{ ml: 1.5, fontSize: "0.74rem" }} color="text.secondary" variant="caption">
                  {session.sessionDate
                    ? moment(session.sessionDate).format("DD MMM YYYY")
                    : ""}
                </Typography>
              </Box>

              {/* Description */}
              {session.sessionDesc && (
                <Typography
                  variant="body2"
                  sx={{
                    mb: 0.8,
                    fontSize: "0.79rem",
                    background: "#f8faff",
                    p: 0.75,
                    borderRadius: 1,
                  }}
                >
                  {session.sessionDesc}
                </Typography>
              )}

              {/* Images */}
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", md: "repeat(3, minmax(0, 1fr))" }, gap: 1 }}>
                {session.attachments?.map((img, i) => (
                  <Box
                    key={i}
                    sx={{
                      width: "100%",
                      aspectRatio: "4 / 3",
                      borderRadius: 1,
                      overflow: "hidden",
                      border: "1px solid var(--color-border-soft)",
                      cursor: "pointer",
                      transition: "0.2s",
                      "&:hover": {
                        transform: "scale(1.01)",
                        boxShadow: 2,
                      },
                    }}
                    onClick={() => window.open(img.url, "_blank")}
                  >
                    <img
                      src={img.url}
                      alt=""
                      width="100%"
                      height="100%"
                      style={{ objectFit: "cover" }}
                    />
                  </Box>
                ))}
              </Box>

              {idx !== sessions.length - 1 && <Divider sx={{ mt: 1.1 }} />}
            </Box>
          ))
        )}
      </DialogContent>
      <DialogActions sx={{ px: 1.25, py: 1, borderTop: "1px solid var(--color-border-soft)" }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            textTransform: "none",
            minHeight: 32,
            px: 1.5,
            fontSize: "0.8rem",
            borderRadius: "8px",
            background: "var(--gradient-primary)",
            boxShadow: "none",
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
